import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';
import { urlBase64ToUint8Array, arrayBufferToBase64Url } from './vapid-helpers';

/**
 * Push subscribe API — Family
 *
 * Wraps the browser's Push API + Service Worker registration into a small set
 * of idempotent helpers. Every helper is safe to call multiple times and
 * defends against missing browser support, missing VAPID config, and missing
 * Supabase config.
 *
 * Flow (CLAUDE.md §6 madde 9, §10):
 *   1. registerServiceWorker()  → idempotent /sw.js registration
 *   2. requestPushPermission()  → must be inside a user gesture (iOS!)
 *   3. subscribeToPush()        → calls pushManager.subscribe() and POSTs
 *                                  the endpoint to push-subscribe Edge Function
 *   4. unsubscribeFromPush()    → reverse of (3); deletes DB row + browser sub
 *
 * iOS Safari quirks:
 *   - PushManager only exists when the app is launched in standalone mode
 *     (i.e. after Add to Home Screen + cold-launch from the Home Screen icon).
 *   - requestPermission() MUST happen synchronously inside a click/tap handler
 *     or it silently returns 'denied'.
 *
 * Privacy (CLAUDE.md §10): we hash the user-agent client-side using SHA-256
 * before sending it to the server, so raw UA strings never leave the device.
 */

export type PushPermission = NotificationPermission; // 'default' | 'granted' | 'denied'

export interface PushCapabilities {
  /** The browser exposes Service Worker + PushManager + Notification APIs. */
  supported: boolean;
  /** Reason the helpers will fail, if any. */
  reason?:
    | 'no-service-worker'
    | 'no-push-manager'
    | 'no-notification'
    | 'no-vapid-key'
    | 'ios-not-standalone';
}

const SERVICE_WORKER_URL = '/sw.js';

/** Unified support check — call once at app boot to short-circuit silent failures. */
export function getPushCapabilities(): PushCapabilities {
  if (typeof window === 'undefined') return { supported: false, reason: 'no-service-worker' };
  if (!('serviceWorker' in navigator)) return { supported: false, reason: 'no-service-worker' };
  if (!('PushManager' in window)) {
    // On iOS this also catches "Safari tab without standalone PWA install".
    if (isIOS() && !isStandalone()) {
      return { supported: false, reason: 'ios-not-standalone' };
    }
    return { supported: false, reason: 'no-push-manager' };
  }
  if (!('Notification' in window)) return { supported: false, reason: 'no-notification' };
  if (!getVapidPublicKey()) return { supported: false, reason: 'no-vapid-key' };
  return { supported: true };
}

/** Read VAPID public key from Vite env. Returns null if missing/placeholder. */
export function getVapidPublicKey(): string | null {
  const key = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (typeof key !== 'string' || key.length === 0) return null;
  if (key.startsWith('BPaste')) return null; // placeholder from .env.example
  return key;
}

/** Are we running as an installed PWA (iOS/Android)? */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS: navigator.standalone (non-standard but still the only signal on Safari)
  if (typeof (navigator as Navigator & { standalone?: boolean }).standalone === 'boolean') {
    if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  }
  // Android/desktop: matchMedia
  if (typeof window.matchMedia === 'function') {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  }
  return false;
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
}

/** Idempotent Service Worker registration. Safe to call on every app boot. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    // If already registered with the same script URL, getRegistration() returns
    // it instead of re-registering — keeps the SW lifecycle stable across reloads.
    const existing = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
    if (existing) return existing;
    return await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' });
  } catch (err) {
    console.error('sw-register-failed', err);
    return null;
  }
}

/** Read current permission without prompting. */
export function getPushPermissionStatus(): PushPermission {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
}

/**
 * Request notification permission. MUST be called inside a user gesture or iOS
 * Safari silently returns 'denied'.
 *
 * Returns the permission state after the prompt resolves. Older Safari versions
 * pass a callback instead of returning a promise; we normalize both shapes.
 */
export async function requestPushPermission(): Promise<PushPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  try {
    const result = Notification.requestPermission();
    if (result instanceof Promise) {
      return await result;
    }
    // Legacy callback-based Safari: the static call returned synchronously
    return Notification.permission;
  } catch (err) {
    console.error('push-permission-request-failed', err);
    return 'denied';
  }
}

/**
 * Subscribe the active SW to Web Push and persist the endpoint via the
 * push-subscribe Edge Function. Returns the resulting `PushSubscription` on
 * success or null on any failure (with a logged reason).
 *
 * Idempotent: if a subscription already exists, it is reused and re-upserted
 * to the server (which refreshes `last_used_at`).
 */
export async function subscribeToPush(): Promise<PushSubscription | null> {
  const caps = getPushCapabilities();
  if (!caps.supported) {
    console.warn('push-not-supported', caps.reason);
    return null;
  }
  if (getPushPermissionStatus() !== 'granted') {
    console.warn('push-permission-not-granted');
    return null;
  }

  const vapidKey = getVapidPublicKey();
  if (!vapidKey) {
    console.warn('push-vapid-key-missing');
    return null;
  }

  const registration = await registerServiceWorker();
  if (!registration) return null;

  let subscription: PushSubscription | null = null;
  try {
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      subscription = existing;
    } else {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }
  } catch (err) {
    console.error('push-subscribe-browser-failed', err);
    return null;
  }

  if (!subscription) return null;

  const persisted = await persistSubscriptionToServer(subscription);
  if (!persisted) {
    // Server write failed — keep the browser subscription so we can retry on
    // next boot rather than burning a fresh subscribe call.
    return subscription;
  }
  return subscription;
}

/** Unsubscribe browser-side and best-effort delete the DB row. */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
  const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
  if (!registration) return false;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return true;

  const endpoint = subscription.endpoint;
  const ok = await subscription.unsubscribe();
  if (!ok) return false;

  // Best-effort DB cleanup. Failure is non-fatal — the server-side fanout will
  // hit a 410 Gone next time and remove the row.
  if (isSupabaseConfigured) {
    try {
      const supabase = getSupabase();
      await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
    } catch (err) {
      console.warn('push-unsubscribe-db-cleanup-failed', err);
    }
  }
  return true;
}

async function persistSubscriptionToServer(subscription: PushSubscription): Promise<boolean> {
  if (!isSupabaseConfigured) {
    console.warn('push-supabase-not-configured');
    return false;
  }
  const p256dh = arrayBufferToBase64Url(subscription.getKey('p256dh'));
  const auth = arrayBufferToBase64Url(subscription.getKey('auth'));
  if (!p256dh || !auth) {
    console.warn('push-subscription-missing-keys');
    return false;
  }

  const userAgentHash = await hashUserAgent();

  try {
    const supabase = getSupabase();
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      console.warn('push-subscribe-no-session');
      return false;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(`${supabaseUrl}/functions/v1/push-subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: { p256dh, auth },
        userAgentHash,
      }),
    });

    if (!response.ok) {
      console.error('push-subscribe-server-error', response.status);
      return false;
    }
    return true;
  } catch (err) {
    console.error('push-subscribe-server-network-error', err);
    return false;
  }
}

async function hashUserAgent(): Promise<string | null> {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return null;
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(navigator.userAgent);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return arrayBufferToBase64Url(hash);
  } catch (err) {
    console.warn('push-ua-hash-failed', err);
    return null;
  }
}
